'use client';

import React, { useState } from 'react';
import { Button } from '@heroui/button';
import { Input } from '@heroui/input';
import Select from "react-select";
import { TzSelectStyles } from '@/shared/styleDefs';
import { AUTH_ARCHITECTURE_OPTIONS, CreateOrganizationRequest, OrganizationDTO } from '@/shared/types';
export interface CreateOrganizationFormProps {
  onSubmit: (data: CreateOrganizationRequest) => Promise<OrganizationDTO>;
  onSuccess: (org: OrganizationDTO) => void;
  onCancel: () => void;
}
const initialForm: CreateOrganizationRequest = {
  name: '',
  contact_email: 'support@tranquility-ai.com',
  auth_architecture: 'cognito-single-pool',
  status: 'active',
  cognito_user_pool_arn: '',
  cognito_domain: '',
  app_client_id: ''
};
export function CreateOrganizationForm({
  onSubmit,
  onSuccess,
  onCancel
}: CreateOrganizationFormProps) {
  const [formData, setFormData] = useState<CreateOrganizationRequest>(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const handleChange = (name: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsSubmitting(true);
      setError(null);
      const org = await onSubmit(formData);
      onSuccess(org);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create organization');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <form onSubmit={handleSubmit} className="flex flex-col gap-6" data-sentry-component="CreateOrganizationForm" data-sentry-source-file="CreateOrganizationForm.tsx">
      {error && <div className="bg-red-900/30 border border-red-700/50 rounded-lg p-4 text-red-300 text-sm">
          {error}
        </div>}

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Basic Information</h2>
        <Input label="Organization Name" name="name" value={formData.name} onValueChange={v => handleChange('name', v)} isRequired placeholder="e.g., City of Springfield" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateOrganizationForm.tsx" />
        <label className="flex text-white/100 font-light tracking-wider text-[14px] m-0 mb-2 p-0">Auth Architecture *</label>
        <Select aria-label="Auth Architecture" styles={{
        ...TzSelectStyles,
        menuPortal: base => ({
          ...base,
          zIndex: 9999,
          color: 'black'
        })
      }} menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined} value={formData.auth_architecture ? {
        value: formData.auth_architecture,
        label: AUTH_ARCHITECTURE_OPTIONS.find(o => o.value === formData.auth_architecture)?.label ?? formData.auth_architecture
      } : null} onChange={(newValue: unknown) => {
        const option = newValue as {
          value: string;
          label: string;
        } | null;
        handleChange('auth_architecture', option?.value ?? '');
      }} className="react-select-container font-inter pr-2 w-full custom-scrollbar2" options={AUTH_ARCHITECTURE_OPTIONS.map(option => ({
        value: option.value,
        label: option.label
      }))} noOptionsMessage={() => "No options available"} data-sentry-element="Select" data-sentry-source-file="CreateOrganizationForm.tsx" />
      </div>

      <div className="bg-slate-800/50 backdrop-blur-xl rounded-lg border border-purple-700/50 p-6 space-y-4">
        <h2 className="text-xl font-semibold text-purple-300 mb-4">Cognito Configuration</h2>
        <Input label="Cognito Domain (optional)" name="cognito_domain" value={formData.cognito_domain} onValueChange={v => handleChange('cognito_domain', v)} placeholder="dev-timepilot02-auth" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateOrganizationForm.tsx" />
        <Input label="Cognito User Pool ARN (optional)" name="cognito_user_pool_arn" value={formData.cognito_user_pool_arn} onValueChange={v => handleChange('cognito_user_pool_arn', v)} placeholder="arn:aws:cognito-idp:us-east-1:..." classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateOrganizationForm.tsx" />
        <Input label="Cognito App Client ID (optional)" name="app_client_id" value={formData.app_client_id} onValueChange={v => handleChange('app_client_id', v)} placeholder="" classNames={{
        input: "box-border text-white",
        inputWrapper: "box-border"
      }} data-sentry-element="Input" data-sentry-source-file="CreateOrganizationForm.tsx" />
      </div>

      <div className="flex justify-end gap-4 pt-2">
        <Button type="button" variant="light" onPress={onCancel} className="text-slate-300 hover:text-white" data-sentry-element="Button" data-sentry-source-file="CreateOrganizationForm.tsx">
          Cancel
        </Button>
        <Button type="submit" isLoading={isSubmitting} isDisabled={!formData.name?.trim()} className="bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg" data-sentry-element="Button" data-sentry-source-file="CreateOrganizationForm.tsx">
          Create Organization
        </Button>
      </div>
    </form>;
}